/**
 * Customer Authentication Types - QR Flow
 * Separate from business user authentication (AuthContext)
 * Phone-based login system for restaurant QR codes
 */

/**
 * Login Request DTO
 */
export interface LoginRequest {
  restaurantId: number;
  phoneNumber: string;
  password: string;
}

/**
 * Register Request DTO
 */
export interface RegisterRequest {
  restaurantId: number;
  fullName: string;
  phoneNumber: string;
  email: string;
  password: string;
  dateOfBirth?: string; // ISO format (YYYY-MM-DD)
}

/**
 * Change Password Request DTO
 */
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Update Profile Request DTO
 */
export interface UpdateProfileRequest {
  fullName?: string;
  email?: string;
  dateOfBirth?: string; // ISO format (YYYY-MM-DD)
  phoneNumber?: string;
  profilePicture?: File; // Profile picture file for upload
  removeProfilePicture?: boolean; // Flag to remove existing profile picture
}

/**
 * Login Response DTO
 */
export interface LoginResponse {
  success: boolean;
  message: string;
  token: string;
  customerAuthId: string;
  phoneNumber: string;
  restaurantId: number;
  role: string;
  customerData?: CustomerProfileDto;
}

/**
 * Register Response DTO
 */
export interface RegisterResponse {
  success: boolean;
  message: string;
  token: string;
  customerAuthId: string;
  phoneNumber: string;
  restaurantId: number;
  role: string;
  customerData?: CustomerProfileDto;
}

/**
 * Customer Profile DTO (GET /api/auth/customer/profile)
 */
export interface CustomerProfileDto {
  id: string; // customerAuthId (UUID)
  fullName: string;
  phoneNumber: string;
  email: string;
  dateOfBirth?: string; // ISO format
  profilePictureUrl?: string;
  restaurantId: number;
  isActive: boolean;
  createdAt: string; // ISO datetime
  updatedAt: string; // ISO datetime
  
  // Points information
  currentPointBalance: number;
  totalPointsEarned: number;
  
  // Tier and spending
  tier?: string; // "Bronze", "Silver", "Gold"
  totalSpent: number;
  visitCount: number;
  
  // Points expiry
  expiryPoints: number;
  expiryDate?: string; // ISO format
  pointsExpireInDays?: number;
  
  // Nested info (legacy fields kept for compatibility)
  loyaltyPoints?: number; // Deprecated - use currentPointBalance
}

/**
 * API Error Response
 */
export interface ApiErrorResponse {
  success: false;
  message: string;
  errors?: Record<string, string[]>;
  statusCode: number;
}

/**
 * Customer Auth Context Type
 */
export interface CustomerAuthContextType {
  // State
  customerAuthId: string | null;
  phoneNumber: string | null;
  restaurantId: number | null;
  customerToken: string | null;
  customerData: CustomerProfileDto | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Actions
  loginCustomer: (
    phoneNumber: string,
    password: string,
    restaurantId: number
  ) => Promise<void>;

  registerCustomer: (registerData: RegisterRequest) => Promise<void>;

  logoutCustomer: () => void;

  updateProfile: (updateData: UpdateProfileRequest) => Promise<void>;

  refreshProfile: () => Promise<void>;

  clearError: () => void;
}

/**
 * Decoded JWT Token Claims
 */
export interface DecodedTokenClaims {
  nameid: string; // customerId (UUID)
  CustomerAuthId: string;
  RestaurantId: number;
  PhoneNumber: string;
  role: string;
  TokenType: string;
  iat: number; // Issued at
  exp: number; // Expiration
}

/**
 * Local Storage Keys
 */
export const CUSTOMER_AUTH_STORAGE_KEYS = {
  TOKEN: "customerAuthToken",
  CUSTOMER_ID: "customerAuthId",
  PHONE_NUMBER: "phoneNumber",
  RESTAURANT_ID: "restaurantId",
  FULL_NAME: "customerFullName",
  EMAIL: "customerEmail",
  PROFILE_PICTURE: "customerProfilePictureUrl",
  LOYALTY_DATA: "loyaltyStatsQR",
  QR_TOKEN: "customerQrToken",
} as const;

/**
 * Session Storage Keys
 */
export const CUSTOMER_SESSION_STORAGE_KEYS = {
  CART: "restaurantCart",
  TABLE_NUMBER: "tableNumber",
} as const;

/**
 * Form Validation Errors
 */
export interface FormValidationErrors {
  phoneNumber?: string;
  password?: string;
  fullName?: string;
  email?: string;
  dateOfBirth?: string;
  confirmPassword?: string;
  termsAccepted?: string;
}

/**
 * Forgot Password Request DTO
 */
export interface ForgotPasswordRequest {
  email: string;
  restaurantId: number;
  clientUri: string;
}

/**
 * Reset Password Request DTO
 */
export interface ResetPasswordRequest {
  email: string;
  restaurantId: number;
  token: string;
  newPassword: string;
}
